import { Routes, Route, Navigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { useAuth } from './contexts/AuthContext';
import { ErrorBoundary } from './components/common/ErrorBoundary';

// Public Pages
import HomePage from './pages/Home';
import LoginPage from './pages/Login';
import RegisterPage from './pages/Register';
import ForgotPasswordPage from './pages/ForgotPassword';
import ResetPasswordPage from './pages/ResetPassword';
import NotFoundPage from './pages/NotFound';

// Dashboard Layout & Pages
import DashboardLayout from './components/layout/DashboardLayout';
import DashboardPage from './pages/Dashboard';

// Client Pages
import { ClientsPage, ClientDetailPage } from './pages/clients';

// Service Pages
import { ServicesPage } from './pages/services';

// Booking Pages
import { BookingsPage, BookingDetailPage } from './pages/bookings';

// Task Pages
import { TasksPage } from './pages/tasks';

// Notes Pages
import { NotesPage } from './pages/notes';

// Projects Pages
import { ProjectsPage, ProjectDetailPage } from './pages/projects';

// Community Hub Page
import { CommunityHubPage } from './pages/community';

// Reminders Page
import { RemindersPage } from './pages/reminders';

// Time Tracking Page
import { TimeTrackingPage } from './pages/time-tracking';

// Calendar Page
import CalendarPage from './pages/calendar/CalendarPage';

// Invoice Pages
import { InvoicesPage, CreateInvoicePage, InvoiceDetailPage } from './pages/invoices';

// Settings Pages
import ProfileSettingsPage from './pages/settings/ProfileSettingsPage';
import BillingSettingsPage from './pages/settings/BillingSettingsPage';
import PaymentSettingsPage from './pages/settings/PaymentSettingsPage';

// Landing Page Editor
import { LandingPageEditor } from './pages/landing-page';

// Leads Pages
import { LeadsPage, LeadDetailPage } from './pages/leads';

// Platform Admin Page
import { PlatformAdminPage } from './pages/platform-admin';

// Contract Pages
import {
  ContractsPage,
  NewContractPage,
  ContractDetailPage,
  ContractTemplatesPage,
  ContractTemplateEditorPage,
} from './pages/contracts';

// Device Management Pages
import { DeviceManagementPage, MyDevicesPage } from './pages/devices';

// Analytics Page
import AnalyticsPage from './pages/AnalyticsPage';

// Public Landing Page
import PublicLandingPage from './pages/public/PublicLandingPage';

// Public Payment Success Page
import PaymentSuccessPage from './pages/public/PaymentSuccessPage';

// Legal Pages
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';

// Notes Quick Capture Provider
import { QuickCaptureProvider } from './components/notes';

// Help System
import { HelpWidgetProvider } from './contexts/HelpWidgetContext';
import { HelpWidget, HelpButton, HelpShortcutListener } from './components/help';

// Crisp Live Chat
import { CrispChat } from './components/crisp';

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Public Route wrapper (redirects to dashboard if logged in)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// Placeholder component for pages not yet built
function ComingSoon({ title }: { title: string }) {
  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight="50vh"
    >
      <h1>{title}</h1>
      <p>This feature is coming soon!</p>
    </Box>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <HelpWidgetProvider>
      <HelpShortcutListener />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<HomePage />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        }
      />

      {/* Legal Pages */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />

      {/* Protected Dashboard Routes */}
      <Route
        element={
          <ProtectedRoute>
            <QuickCaptureProvider>
              <DashboardLayout />
            </QuickCaptureProvider>
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/clients" element={<ClientsPage />} />
        <Route path="/clients/:id" element={<ClientDetailPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/bookings" element={<BookingsPage />} />
        <Route path="/bookings/:id" element={<BookingDetailPage />} />
        <Route path="/tasks" element={<TasksPage />} />
        <Route path="/notes" element={<NotesPage />} />
        <Route path="/invoices" element={<InvoicesPage />} />
        <Route path="/invoices/new" element={<CreateInvoicePage />} />
        <Route path="/invoices/:id" element={<InvoiceDetailPage />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/projects/:id" element={<ProjectDetailPage />} />
        <Route path="/reminders" element={<RemindersPage />} />
        <Route path="/time-tracking" element={<TimeTrackingPage />} />
        <Route path="/contracts" element={<ContractsPage />} />
        <Route path="/contracts/new" element={<NewContractPage />} />
        <Route path="/contracts/templates" element={<ContractTemplatesPage />} />
        <Route path="/contracts/templates/new" element={<ContractTemplateEditorPage />} />
        <Route path="/contracts/templates/:id/edit" element={<ContractTemplateEditorPage />} />
        <Route path="/contracts/:id" element={<ContractDetailPage />} />
        <Route path="/messages" element={<ComingSoon title="Messages" />} />
        <Route path="/landing-page" element={<LandingPageEditor />} />
        <Route path="/leads" element={<LeadsPage />} />
        <Route path="/leads/:id" element={<LeadDetailPage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/community" element={<CommunityHubPage />} />
        <Route path="/settings" element={<ProfileSettingsPage />} />
        <Route path="/settings/profile" element={<ProfileSettingsPage />} />
        <Route path="/settings/billing" element={<BillingSettingsPage />} />
        <Route path="/settings/payments" element={<PaymentSettingsPage />} />
        <Route path="/settings/devices" element={<MyDevicesPage />} />
        <Route path="/platform-admin" element={<PlatformAdminPage />} />
        <Route path="/admin/devices" element={<DeviceManagementPage />} />
      </Route>

      {/* Landing Page Preview (authenticated) - renders same as public but uses preview API */}
      <Route
        path="/preview/landing-page"
        element={
          <ProtectedRoute>
            <PublicLandingPage previewMode />
          </ProtectedRoute>
        }
      />

      {/* Public Payment Success Page - MUST be before /:slug catch-all */}
      <Route path="/pay/success/:invoiceId" element={<PaymentSuccessPage />} />

      {/* Public Landing Page - MUST be before 404 catch-all */}
      {/* This route catches /:slug for user landing pages */}
      <Route path="/:slug" element={<PublicLandingPage />} />

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>

    {/* Help System */}
    <HelpWidget />
    <HelpButton />

    {/* Crisp Live Chat - for human support */}
    <CrispChat />
  </HelpWidgetProvider>
    </ErrorBoundary>
  );
}

export default App;
